import MapView from '@arcgis/core/views/MapView';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import DistanceMeasurement2DViewModel from '@arcgis/core/widgets/DistanceMeasurement2D/DistanceMeasurement2DViewModel';
import AreaMeasurement2DViewModel from '@arcgis/core/widgets/AreaMeasurement2D/AreaMeasurement2DViewModel';
import Symbol from '@arcgis/core/symbols/Symbol';
import Point from '@arcgis/core/geometry/Point';
import { MeasureInputResult, MeasureInputType } from './MeasureInputResult';
import { SketchInputResult, SketchInputType } from './SketchInputResult';
import { observe } from 'mobx';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import Color from '@arcgis/core/Color';
import Font from '@arcgis/core/symbols/Font';
import { TextSketchInputProperties } from './TextSketchInputProperties';
import { CancelTracker } from './CancelTracker';
import {
  ISelectedMeasurementUnits,
  MeasureDistanceUnits,
} from 'cd-eft/components/common/MeasureTools/MeasureToolboxView';

// export class SketchInputProps {
//   symbol?: Symbol = undefined;
// }

export type MeasureFeedbackHandler = (e: MeasureInputResult) => void;

export interface IMapViewKeyPressEventData {
  key: string;
}

export interface IMapViewClickEventData {
  x: number;
  y: number;
  mapPoint: Point;
  screenPoint: __esri.MapViewScreenPoint;
}

export function measureAsync(
  mapView: MapView,
  type: MeasureInputType,
  selectedUnit: ISelectedMeasurementUnits,
  cancelTracker?: CancelTracker,
  measureFeedbackHandler?: MeasureFeedbackHandler
): Promise<MeasureInputResult> {
  let resolver: (result: MeasureInputResult) => void;
  let rejecter: (reason?: any) => void;
  const promise = new Promise<MeasureInputResult>((resolve, reject) => {
    resolver = resolve;
    rejecter = reject;
  });

  let measureVm: AreaMeasurement2DViewModel | DistanceMeasurement2DViewModel;
  if (type === 'measure-area') {
    const areaUnit = selectedUnit.area;
    measureVm = new AreaMeasurement2DViewModel({
      view: mapView,
      unit: areaUnit,
    });
    // console.log('Area unit: ', measureVm.unit);
  } else if (type === 'measure-distance') {
    const distanceUnit = selectedUnit.distance;
    measureVm = new DistanceMeasurement2DViewModel({
      view: mapView,
      unit: distanceUnit,
    });
  } else {
    throw new Error('unexpected input: type');
  }

  measureVm.start();

  const watchHandle = (measureVm as any).watch('state', function (
    newValue: 'disabled' | 'ready' | 'measuring' | 'measured'
  ) {
    if (newValue === 'measuring') {
      //do nothing
    } else if (newValue === 'measured') {
      const measurement = measureVm.measurement;

      let result: MeasureInputResult;
      if (type === 'measure-distance') {
        result = convertDistanceMeasurementToMeasureInputResult(
          measurement as __esri.DistanceMeasurement2DViewModelMeasurement,
          measureVm.measurementLabel as string
        );
      } else {
        //(type === 'measure-area')
        result = convertAreaMeasurementToMeasureInputResult(
          measurement as __esri.AreaMeasurement2DViewModelMeasurement,
          measureVm.measurementLabel as any,
          selectedUnit.perimeter
        );
      }
      cleanup();
      resolver(result);
    } else {
      // this condition happens when user presses 'ESC' key after tool started, but after first map click
      cleanup();
      rejecter(new Error('Action interrupted by user.'));
    }
  });

  const watchHandle2 = (measureVm as any).watch('measurement', function (newValue) {
    // // spies on measure layers to figure out the default graphic symbol properties
    // console.log('allLayers', mapView.map.allLayers);
    // const distanceLineSymbol = mapView.map.allLayers._items[3].graphics._items[0].symbol.toJSON();
    // const distanceTextSymbol = mapView.map.allLayers._items[3].graphics._items[1].symbol.toJSON();
    // console.log('distanceLineSymbol', JSON.stringify(distanceLineSymbol));
    // console.log('distanceTextSymbol', JSON.stringify(distanceTextSymbol));

    // // spies on measure layers to figure out the default graphic symbol properties
    // console.log('allLayers', mapView.map.allLayers);
    // const areaFillSymbol = mapView.map.allLayers._items[3].graphics._items[0].symbol.toJSON();
    // const areaLineSymbol = mapView.map.allLayers._items[3].graphics._items[1].symbol.toJSON();
    // const areaTextSymbol = mapView.map.allLayers._items[3].graphics._items[2].symbol.toJSON();
    // console.log('areaFillSymbol', JSON.stringify(areaFillSymbol));
    // console.log('areaLineSymbol', JSON.stringify(areaLineSymbol));
    // console.log('areaTextSymbol', JSON.stringify(areaTextSymbol));

    // console.log('newValue', newValue);

    if (newValue && measureFeedbackHandler) {
      let feedbackResult: MeasureInputResult;
      if (type === 'measure-distance') {
        feedbackResult = convertDistanceMeasurementToMeasureInputResult(
          newValue as __esri.DistanceMeasurement2DViewModelMeasurement,
          measureVm.measurementLabel as string
        );
      } else {
        //(type === 'measure-area')
        feedbackResult = convertAreaMeasurementToMeasureInputResult(
          newValue as __esri.AreaMeasurement2DViewModelMeasurement,
          measureVm.measurementLabel as any,
          selectedUnit?.perimeter
        );
      }
      measureFeedbackHandler(feedbackResult);
    }

    if (measureVm.state === 'ready' && newValue === undefined) {
      // this condition happens when user presses 'ESC' key after tool started, but before first map click
      cleanup();
      rejecter(new Error('Action interrupted by user.'));
    }
  });

  if (cancelTracker) {
    cancelTracker.when(() => {
      cleanup();
      rejecter(new Error('Action interrupted by user.'));
    });
  }

  // define local scope function to tear down resources and prevent possible memory leaks
  function cleanup() {
    if (watchHandle) {
      watchHandle.remove();
      //(watchHandle as unknown) = undefined;
    }
    if (watchHandle2) {
      watchHandle2.remove();
      //(watchHandle2 as unknown) = undefined;
    }
    if (measureVm) {
      measureVm.clear();
      measureVm.view = undefined as any;
      (measureVm as any).destroy();
      (measureVm as unknown) = undefined;
    }
  }

  return promise;
}

export function sketchAsync(
  mapView: MapView,
  type: SketchInputType,
  cancelTracker?: CancelTracker,
  textSketchInputProps?: TextSketchInputProperties
): Promise<SketchInputResult> {
  let resolver: (value: SketchInputResult) => void;
  let rejecter: (reason?: any) => void;
  const promise = new Promise<SketchInputResult>((resolve, reject) => {
    resolver = resolve;
    rejecter = reject;
  });

  const layer = new GraphicsLayer(); // does not need to be added to map to receive sketch results
  const sketchVm = new SketchViewModel({ view: mapView, layer: layer });

  let textSymbol;
  if (type === 'sketch-text') {
    textSymbol = new TextSymbol();
    textSymbol.haloColor = new Color('white');
    textSymbol.haloSize = 0.75;
    textSymbol.verticalAlignment = 'baseline';
    textSymbol.horizontalAlignment = 'left';
    if (textSketchInputProps) {
      textSymbol.text = textSketchInputProps.text;
      textSymbol.font = new Font({ size: textSketchInputProps.size, weight: 'bold', family: 'Arial' });
    }
    sketchVm.pointSymbol = textSymbol;
  }

  switch (type) {
    case 'sketch-point':
      sketchVm.create('point');
      break;
    case 'sketch-multipoint':
      sketchVm.create('multipoint');
      break;
    case 'sketch-polyline':
      sketchVm.create('polyline');
      break;
    case 'sketch-polygon':
      sketchVm.create('polygon');
      break;
    case 'sketch-circle':
      sketchVm.create('circle');
      break;
    case 'sketch-rectangle':
      sketchVm.create('rectangle');
      break;
    case 'sketch-text':
      sketchVm.create('point');
      break;
    default:
      throw new Error('unexpected input: type');
  }

  const watchHandle = sketchVm.on('create', (e) => {
    if (e.state === 'complete') {
      if (e.graphic) {
        const geometry = e.graphic.geometry.clone();
        const result = new SketchInputResult(type, geometry, textSymbol, textSketchInputProps);
        cleanup();
        resolver(result);
      }
    }
    if (e.state === 'cancel') {
      cleanup();
      rejecter(new Error('Action interrupted by user.'));
    }
  });

  let stopObservingText;
  let stopObservingSize;
  if (textSketchInputProps) {
    // listen to observable changes to update the textSymbol properties
    stopObservingText = observe(textSketchInputProps, 'text', (e) => {
      textSymbol.text = e.newValue; //update the symbol with new value
    });
    stopObservingSize = observe(textSketchInputProps, 'size', (e) => {
      textSymbol.font.size = e.newValue;
    });
  }
  if (cancelTracker) {
    cancelTracker.when(() => {
      cleanup();
      rejecter(new Error('Action interrupted by user.'));
    });
  }

  // define local scope function to tear down resources and prevent possible memory leaks
  function cleanup() {
    if (stopObservingText) {
      stopObservingText(); //dispose of the observer
    }
    if (stopObservingSize) {
      stopObservingSize(); //dispose of the observer
    }
    if (watchHandle) {
      watchHandle.remove();
      // (watchHandle as unknown) = undefined;
    }
    if (layer) {
      layer.removeAll();
      layer.destroy();
      // (layer as unknown) = undefined;
    }
    if (sketchVm) {
      sketchVm.cancel();
      sketchVm.layer = undefined as any;
      sketchVm.view = undefined as any;
      (sketchVm as any).destroy();
      // (sketchVm as unknown) = undefined;
    }
  }

  return promise;
}

export type MapViewGestureType = 'click' | 'double-click' | 'hold' | 'immediate-click' | 'immediate-double-click';

export function getMapViewGesture(
  mapView: MapView,
  type: MapViewGestureType,
  cancelTracker?: CancelTracker,
  pointerMoveHandler?: (e: any) => void
): Promise<IMapViewClickEventData> {
  let resolver: (value: IMapViewClickEventData) => void;
  let rejecter: (reason?: any) => void;
  const promise = new Promise<IMapViewClickEventData>((resolve, reject) => {
    resolver = resolve;
    rejecter = reject;
  });

  const eventHandle1 = mapView.on(type, (e: IMapViewClickEventData) => {
    cleanup();
    resolver(e);
  });

  //listen for "Escape" key, cancel operation if pressed
  const eventHandle2 = mapView.on('key-down', (e: IMapViewKeyPressEventData) => {
    if (e.key === 'Escape') {
      cleanup();
      rejecter(new Error('Action interrupted by user.'));
    }
  });

  let eventHandle3;
  if (pointerMoveHandler) {
    eventHandle3 = mapView.on('pointer-move', pointerMoveHandler);
  }

  if (cancelTracker) {
    cancelTracker.when(() => {
      cleanup();
      rejecter(new Error('Action interrupted by user.'));
    });
  }

  // define local scope function to tear down resources and prevent possible memory leaks
  function cleanup() {
    if (eventHandle1) {
      eventHandle1.remove();
      // (eventHandle1 as unknown) = undefined;
    }
    if (eventHandle2) {
      eventHandle2.remove();
      // (eventHandle2 as unknown) = undefined;
    }
    if (eventHandle3) {
      eventHandle3.remove();
      (eventHandle3 as unknown) = undefined;
    }
  }

  mapView.focus();

  return promise;
}

export function convertDistanceMeasurementToMeasureInputResult(
  measurement: __esri.DistanceMeasurement2DViewModelMeasurement,
  label: string
): MeasureInputResult {
  const result = new MeasureInputResult('measure-distance', measurement.geometry, label, undefined, undefined);

  return result;
}

export function convertAreaMeasurementToMeasureInputResult(
  measurement: __esri.AreaMeasurement2DViewModelMeasurement,
  label: { area: string; perimeter: string },
  perimeterUnit: MeasureDistanceUnits
): MeasureInputResult {
  //
  //replace perimeter lable with feet instead of miles
  let perimeterStr = label.perimeter;
  if (measurement.perimeter) {
    switch (perimeterUnit) {
      case 'feet':
        const feet = metersToFeet(measurement.perimeter);
        perimeterStr = feet.toLocaleString('en-US', {
          style: 'unit',
          unit: 'foot',
          unitDisplay: 'short',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        break;
      case 'kilometers':
        const km = metersToKilometers(measurement.perimeter);
        perimeterStr = km.toLocaleString('en-US', {
          style: 'unit',
          unit: 'kilometer',
          unitDisplay: 'short',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        break;
      case 'miles':
        const miles = metersToMiles(measurement.perimeter);
        perimeterStr = miles.toLocaleString('en-US', {
          style: 'unit',
          unit: 'mile',
          unitDisplay: 'short',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        break;
      case 'meters':
      default:
        break;
    }
  }

  const result = new MeasureInputResult('measure-area', measurement.geometry, undefined, label.area, perimeterStr);

  return result;
}

function metersToFeet(meters: number) {
  const feet = meters * 3.2808333333;
  return feet;
}

function metersToMiles(meters: number) {
  const miles = meters * 0.0006213712;
  return miles;
}

function metersToKilometers(meters: number) {
  const km = meters / 1000;
  return km;
}
